import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/* Stock-Bilder als Platzhalter für echte Event-Fotos.
   Ersetzen Sie diese URLs durch eigene Fotos von Atelier 77. */
const GALLERY_IMAGES = [
  { id: 1, cat: "hochzeiten", src: "https://images.unsplash.com/photo-1660243353143-f77ba06d4913?auto=format&fit=crop&w=700&q=80", title: "Hochzeitsdekoration mit Laterne" },
  { id: 2, cat: "hochzeiten", src: "https://images.unsplash.com/photo-1736238952214-88853aa68b2b?auto=format&fit=crop&w=700&q=80", title: "Festtafel mit Kerzen" },
  { id: 3, cat: "hochzeiten", src: "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?auto=format&fit=crop&w=700&q=80", title: "Hochzeitsfeier Tischdeko" },
  { id: 4, cat: "hochzeiten", src: "https://images.unsplash.com/photo-1736238952236-8f529a07aea8?auto=format&fit=crop&w=700&q=80", title: "Romantisches Ambiente" },
  { id: 5, cat: "geburtstage", src: "https://images.unsplash.com/photo-1607977166054-ba266c446ba3?auto=format&fit=crop&w=700&q=80", title: "Geburtstagsfeier mit Lichtern" },
  { id: 6, cat: "geburtstage", src: "https://images.unsplash.com/photo-1631857455684-a54a2f03665f?auto=format&fit=crop&w=700&q=80", title: "Geburtstagstorte" },
  { id: 7, cat: "firmenevents", src: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=700&q=80", title: "Firmenpräsentation" },
  { id: 8, cat: "firmenevents", src: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=700&q=80", title: "Betriebsfest" },
  { id: 9, cat: "firmenevents", src: "https://images.unsplash.com/photo-1779300007516-eebee9151c19?auto=format&fit=crop&w=700&q=80", title: "Industrieloft-Event" },
  { id: 10, cat: "konzerte", src: "https://images.unsplash.com/photo-1776090188612-a2dab458ce14?auto=format&fit=crop&w=700&q=80", title: "Leere Eventhalle" },
  { id: 11, cat: "konzerte", src: "https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&w=700&q=80", title: "Kunstausstellung" },
  { id: 12, cat: "konzerte", src: "https://images.pexels.com/photos/2559741/pexels-photo-2559741.jpeg?auto=compress&cs=tinysrgb&w=700", title: "Galerieambiente" },
];

const FILTERS = [
  { key: "alle", label: "Alle" },
  { key: "hochzeiten", label: "Hochzeiten" },
  { key: "geburtstage", label: "Geburtstage" },
  { key: "firmenevents", label: "Firmenevents" },
  { key: "konzerte", label: "Konzerte & Kunst" },
];

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

export default function Gallery() {
  const [activeFilter, setActiveFilter] = useState("alle");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered =
    activeFilter === "alle"
      ? GALLERY_IMAGES
      : GALLERY_IMAGES.filter((img) => img.cat === activeFilter);

  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
  };
  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
  };

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      data-testid="gallery-page"
    >
      <div className="pt-32 pb-12 px-6 md:px-12 max-w-7xl mx-auto text-center">
        <p
          className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Impressionen
        </p>
        <h1
          className="text-5xl md:text-7xl font-extrabold uppercase text-[#F2EDE8]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="gallery-title"
        >
          Galerie
        </h1>
        <p
          className="text-[#A19D98] mt-4 max-w-xl mx-auto text-sm"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Stock-Bilder als Platzhalter. Hier entstehen bald echte Veranstaltungsfotos
          aus dem Atelier 77.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="px-6 md:px-12 max-w-7xl mx-auto mb-10">
        <div
          className="flex flex-wrap gap-2 justify-center"
          data-testid="gallery-filters"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-5 py-2 text-xs tracking-[0.2em] uppercase transition-all duration-200 border ${
                activeFilter === f.key
                  ? "border-[#C1440E] text-[#C1440E] bg-[#C1440E]/5"
                  : "border-[#F2EDE8]/10 text-[#A19D98] hover:border-[#F2EDE8]/30 hover:text-[#F2EDE8]"
              }`}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Masonry Grid */}
      <div className="px-6 md:px-12 max-w-7xl mx-auto pb-24">
        <div
          className="columns-1 sm:columns-2 lg:columns-3 gap-4"
          data-testid="gallery-grid"
        >
          {filtered.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="mb-4 relative group cursor-pointer overflow-hidden break-inside-avoid"
              onClick={() => setLightboxIndex(i)}
              data-testid={`gallery-item-${img.id}`}
            >
              <img
                src={img.src}
                alt={img.title}
                className="w-full block object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span
                  className="text-[#F2EDE8] text-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {img.title}
                </span>
              </div>
              <div className="absolute inset-0 border border-[#C1440E] opacity-0 group-hover:opacity-60 transition-opacity duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] bg-[#141414]/96 backdrop-blur-xl flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              className="absolute top-5 right-5 text-[#F2EDE8]/60 hover:text-[#F2EDE8] z-10 p-2 transition-colors"
              onClick={closeLightbox}
              data-testid="lightbox-close"
              aria-label="Schließen"
            >
              <X size={28} />
            </button>
            <button
              className="absolute left-4 md:left-8 text-[#F2EDE8]/60 hover:text-[#F2EDE8] z-10 p-3 transition-colors"
              onClick={prevImage}
              data-testid="lightbox-prev"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft size={32} />
            </button>
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="max-h-[85vh] max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filtered[lightboxIndex]?.src}
                alt={filtered[lightboxIndex]?.title}
                className="w-full h-full object-contain max-h-[80vh]"
              />
              <p
                className="text-center text-[#A19D98] text-sm mt-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {filtered[lightboxIndex]?.title}
              </p>
            </motion.div>
            <button
              className="absolute right-4 md:right-8 text-[#F2EDE8]/60 hover:text-[#F2EDE8] z-10 p-3 transition-colors"
              onClick={nextImage}
              data-testid="lightbox-next"
              aria-label="Nächstes Bild"
            >
              <ChevronRight size={32} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.main>
  );
}
